import { inject, Injectable } from '@angular/core';
import { account, client } from '../helpers/appwrite';
import { Account, ID, Models } from 'appwrite';
import { NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  first,
  firstValueFrom,
  Observable,
} from 'rxjs';
import { UserType } from '@shared/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  account: typeof account;

  readonly user$: Observable<Models.User<Models.Preferences> | null>;
  private readonly _user$: BehaviorSubject<Models.User<Models.Preferences> | null> =
    new BehaviorSubject<Models.User<Models.Preferences> | null>(null);

  readonly userType$: Observable<UserType | null>;
  private readonly _userType$: BehaviorSubject<UserType | null> =
    new BehaviorSubject<UserType | null>(null);

  readonly isAnonymousSession$: Observable<boolean>;
  private readonly _isAnonymousSession$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  private readonly _router = inject(Router);

  get user(): Models.User<Models.Preferences> | null {
    return this._user$.getValue();
  }

  get userType(): UserType | null {
    return this._userType$.getValue();
  }

  constructor() {
    this.account = account;
    this.user$ = this._user$.asObservable();
    this.isAnonymousSession$ = this._isAnonymousSession$.asObservable();
    this.userType$ = this._userType$.asObservable();
  }

  async register(
    email: string,
    password: string,
    fullName: string
  ): Promise<Models.User<Models.Preferences>> {
    const account = new Account(client);
    return account.create(ID.unique(), email, password, fullName);
  }

  // Auth methods
  async login(email: string, password: string): Promise<Models.Session> {
    return await this.account.createEmailPasswordSession(email, password);
  }

  async logout(): Promise<void> {
    // return await this.account.deleteSession('current');

    try {
      // Try to get the current session
      await account.getSession('current');

      // If it succeeds, session exists → delete it
      await account.deleteSession('current');
      console.log('Session deleted.');
    } catch (err) {
      // console.error('Unexpected error checking session:', err);
    }
  }

  async refresh(): Promise<void> {
    try {
      const event = await firstValueFrom(
        this._router.events.pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          ),
          first()
        )
      );

      const currentUrl = event.url;

      const user = await this.getCurrentUser();

      // if (user && currentUrl === '/login') {
      if (
        user &&
        this.userType !== UserType.ANONYMOUS &&
        (currentUrl === '/login' || currentUrl === '/')
      ) {
        this._router.navigate(['/home']);
      }
      // else: stay on current page
    } catch (error) {
      this._router.navigate(['/login']);
    }
  }

  async getCurrentUser(): Promise<Models.User<Models.Preferences> | null> {
    try {
      const user = await this.account.get();
      this._user$.next(user);

      // Set user type
      // [todo] Should optimize this to avoid unnecessary calls
      await this.isAnonymousSession();
      await this.setUserType();

      return user;
    } catch (error) {
      return null;
    }
  }

  async getCurrentSession(): Promise<Models.Session | null> {
    try {
      const session = await this.account.getSession('current');

      // [todo] I thought of keeping the session in a BehaviorSubject,
      // but it seems very tricky to manage
      // in some cases we have to refresh the session
      return session;
    } catch (error) {
      return null;
    }
  }

  async startAnonymousSession(): Promise<Models.Session> {
    const account = new Account(client);
    return account.createAnonymousSession();
  }

  private async isAnonymousSession(): Promise<boolean> {
    let user = this._user$.getValue();
    if (!user) {
      user = await this.getCurrentUser();
    }

    const session = await this.getCurrentSession();

    const isAnonymousSession = !!user && session?.provider === 'anonymous';
    this._isAnonymousSession$.next(isAnonymousSession);

    return isAnonymousSession;
  }

  private async setUserType(): Promise<void> {
    const isAnonymousSession = this._isAnonymousSession$.getValue();
    if (isAnonymousSession) {
      this._userType$.next(UserType.ANONYMOUS);
      return;
    }

    const user = this._user$.getValue();
    const label = user?.labels?.[0] || null;
    if (label) {
      this._userType$.next(UserType.PROPERTY_MANAGER);
      return;
    }

    this._userType$.next(UserType.INVESTOR);
  }

  async updatePhoneNumber(
    phone: string,
    password: string
  ): Promise<Models.User<Models.Preferences>> {
    const account = new Account(client);
    return account.updatePhone(phone, password);
  }
}
